/**
 * `LinkDaEntidade`: o nome de um registro leva à ficha dele, SEMPRE em nova aba (item 09 do
 * lote de 24/09 · D-136 · DS-533).
 *
 * É a casca: lê a permissão (`usePode`) e o endereço atual do roteador, e aplica a resolução
 * pura de `link-da-entidade-regras.ts`, que é o mapa único tipo → rota. Sem tipo, sem id, sem
 * `ver` no módulo da ficha, ou na própria ficha, o nome é um `<span>` de texto, nunca um link
 * morto. Nos outros casos é o `<Link>` do roteador (um `<a href>` de verdade: botão do meio e
 * Ctrl+clique continuam valendo) com `target="_blank"`.
 *
 * O resto do contrato, e o porquê de cada peça:
 *  - `rel="noopener noreferrer"`: a aba nova não alcança a que a abriu;
 *  - `aria-describedby` aponta para UM `<span hidden>` do shell ("abre em nova aba"): o nome
 *    acessível do link não muda, e o texto do contêiner também não;
 *  - o clique e o Enter/Espaço param a PROPAGAÇÃO, sem `preventDefault`: o navegador abre a
 *    aba, e a linha ou o cartão clicável por baixo não navega junto (EI-33);
 *  - `draggable={false}`: dentro de um cartão arrastável, o gesto arrasta o cartão, e não a URL;
 *  - o hover é o do sistema (`dv-link`, D-132): só a cor, nunca sublinhado nem fundo. Quem
 *    chama passa tipografia e espaçamento em `className`, nunca cor de hover;
 *  - sobre `bg-ink`/`bg-navy`, `sobreEscuro` troca o tom (hover e anel brancos, EI-31);
 *  - link de entidade nunca fica dentro de ancestral com `overflow` recortado: quando o nome
 *    trunca, quem trunca é o próprio link (`className="truncate"`), e o anel de foco não é
 *    cortado (EI-15).
 *
 * A linha de listagem (relato G1 · D-155 · DS-569):
 *  - dentro de `LinhaDaListagem` o nome de OUTRO registro é texto, sozinho: a linha avisa
 *    pelo contexto (`ContextoDaLinha`), e o clique no nome abre a linha. `Modal` e `Gaveta`
 *    zeram o contexto, e lá dentro o nome volta a ser link;
 *  - `alvoDaLinha` é o registro DA PRÓPRIA linha, e a única exceção de aba: mesmo mapa, mesma
 *    permissão e mesma regra da própria ficha, na MESMA aba, sem a descrição de nova aba e
 *    sem parar a propagação. `"ficha"` empilha histórico; `"gaveta"` troca o endereço da
 *    gaveta da mesma página. Ele leva a marca `data-alvo-da-linha`, que a linha lê, e diz aos
 *    filhos que eles estão dentro da linha (o item inteiro do Dashboard contém outros nomes).
 */
import { useCallback, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { usePode, useValoresFinanceiros } from '../shell/contexto-de-permissoes.tsx'
import { useTextos } from '../textos.ts'
import { ContextoDaLinha, useDentroDaLinha } from './linha-da-listagem-contexto.ts'
import {
  ATRIBUTOS_DE_NOVA_ABA,
  ID_DA_DESCRICAO_NOVA_ABA,
  atributosDoLink,
  classesDoLink,
  hrefDaEntidade,
  mapaDoTipo,
  resolverLinkDaEntidade,
  type AlvoDaLinha,
  type TipoDaEntidade,
  type TomDoLink,
} from './link-da-entidade-regras.ts'
import { TEXTOS_DO_LINK_DA_ENTIDADE } from './link-da-entidade-textos.ts'

/** O id do `<span hidden>` do shell que diz "abre em nova aba" (mora nas regras, sem DOM). */
export { ID_DA_DESCRICAO_NOVA_ABA }

/**
 * A descrição única, renderizada UMA vez no shell, dentro das preferências (o idioma).
 * Todo `LinkDaEntidade` aponta para ela; nenhum repete o texto.
 */
export function DescricaoDeNovaAba(): React.JSX.Element {
  const t = useTextos(TEXTOS_DO_LINK_DA_ENTIDADE)
  return <span id={ID_DA_DESCRICAO_NOVA_ABA} hidden>{t('novaAba')}</span>
}

export interface PropsDoLinkDaEntidade {
  /** `null`: texto. */
  readonly tipo: TipoDaEntidade | null
  /** O uuid; na conta contábil, o código. Vazio, `null` ou `undefined`: texto. */
  readonly id: string | null | undefined
  /** O rótulo, exatamente como a tela já o escreve. */
  readonly children: ReactNode
  /** `navy` (padrão) pinta o repouso; `herdado` deixa a cor da célula e vira navy no hover. */
  readonly tom?: TomDoLink
  /** Sobre `bg-ink`/`bg-navy`: hover e anel brancos (EI-31). */
  readonly sobreEscuro?: boolean
  /** Tipografia e espaçamento de quem chama; nunca cor de hover. */
  readonly className?: string | undefined
  readonly title?: string | undefined
  /** `data-teste`, nos dois modos: o nome continua achável quando é texto. */
  readonly teste?: string | undefined
  /** Só na versão de orçamento: abre a 8.2 no escopo de gastos. */
  readonly escopoDoOrcamento?: 'gastos' | 'financeiro' | undefined
  /**
   * O registro DA PRÓPRIA linha de listagem, na mesma aba (D-155, DS-569): `ficha` empilha
   * histórico; `gaveta` troca o endereço da gaveta ou do modal da mesma página.
   */
  readonly alvoDaLinha?: AlvoDaLinha | undefined
}

/** Enter e Espaço ativam o link; o contêiner com teclado próprio não ouve (EI-33). */
function pararTecla(evento: KeyboardEvent<HTMLAnchorElement>): void {
  if (evento.key === 'Enter' || evento.key === ' ') evento.stopPropagation()
}

/** O clique abre a aba; a linha ou o cartão por baixo não navega junto. */
function pararClique(evento: MouseEvent<HTMLAnchorElement>): void {
  evento.stopPropagation()
}

export function LinkDaEntidade({
  tipo,
  id,
  children,
  tom = 'navy',
  sobreEscuro = false,
  className,
  title,
  teste,
  escopoDoOrcamento,
  alvoDaLinha,
}: PropsDoLinkDaEntidade): React.JSX.Element {
  const pode = usePode()
  const veValores = useValoresFinanceiros()
  const local = useLocation()
  const dentroDeLinha = useDentroDaLinha()
  const resolucao = resolverLinkDaEntidade({
    tipo,
    id,
    pode,
    veValores,
    local: { pathname: local.pathname, search: local.search, hash: local.hash },
    escopoDoOrcamento,
    dentroDeLinha,
    alvoDaLinha,
  })
  // O alvo da linha diz aos filhos que eles estão dentro dela, mesmo quando vira texto.
  const conteudo = alvoDaLinha === undefined ? children : <ContextoDaLinha.Provider value>{children}</ContextoDaLinha.Provider>
  if (resolucao.modo === 'texto') {
    return <span className={className} data-teste={teste} title={title}>{conteudo}</span>
  }
  const { destino } = resolucao
  const classes = className === undefined || className === '' ? classesDoLink(tom, sobreEscuro) : `${classesDoLink(tom, sobreEscuro)} ${className}`
  // Só o link em nova aba para a propagação: o alvo da linha deixa o clique subir, e a
  // linha o ignora por vir de um controle.
  const parar = resolucao.aba === 'nova' ? { onClick: pararClique, onKeyDown: pararTecla } : {}
  return (
    <Link
      to={destino.to}
      search={destino.search}
      {...(destino.hash === undefined ? {} : { hash: destino.hash })}
      {...atributosDoLink(resolucao)}
      draggable={false}
      data-entidade={tipo ?? undefined}
      data-teste={teste}
      title={title}
      className={classes}
      {...parar}
    >
      {conteudo}
    </Link>
  )
}

/**
 * A mesma regra para quem não pode ser `<a>`: a ação de um toast é um botão de callback
 * (`toast.tsx`). Devolve a função que abre a ficha em nova aba, ou `null` sem id ou sem
 * permissão, e aí a ação não se oferece.
 */
export function useAbrirEmNovaAba(): (tipo: TipoDaEntidade | null, id: string | null | undefined) => (() => void) | null {
  const pode = usePode()
  const veValores = useValoresFinanceiros()
  return useCallback((tipo: TipoDaEntidade | null, id: string | null | undefined) => {
    // O tipo que o mapa não conhece não abre nada, em vez de derrubar a tela (AUD31-06).
    const mapa = mapaDoTipo(tipo)
    if (mapa === null || !pode(mapa.modulo, 'ver')) return null
    if (mapa.valores === true && !veValores(mapa.modulo)) return null
    const href = hrefDaEntidade(tipo, id)
    if (href === null) return null
    return () => {
      window.open(href, ATRIBUTOS_DE_NOVA_ABA.target, 'noopener,noreferrer')
    }
  }, [pode, veValores])
}
